// Indicadores do Planejamento — porte das fórmulas do backend
// (ReportBuilderService.QueryComputedAsync / IndicatorByCategoryHandlers / FinancialGroupByCategoryHandlers).
//
// Grupo A (soma das categorias do indicador, config SyncKPIDRE): RB, RL, LB, EBITDA, LL, IMPOSTOS
// Grupo B (soma das categorias do grupo financeiro, config SyncFinancialGroup): CF, CV, DF, DV, INV
// Grupo C (fórmulas sobre A e B), denominador Receita Bruta como no backend — exceto a
// Margem Líquida, que no Planejamento é Lucro Líquido / Receita Líquida (decisão do produto).
// Divisão por zero = 0; razões truncadas em 4 casas (Round(v, 4, ToZero) no backend) e exibidas ×100.

import { INDICATOR_KEYS, FINANCIAL_GROUP_KEYS } from './dataset';
import { MONTH_INDEXES, zeros12 } from './periods';

export const BASE_INDICATOR_KEYS = [...INDICATOR_KEYS, ...FINANCIAL_GROUP_KEYS];

const trunc4 = (v) => Math.trunc((Number(v) || 0) * 10000) / 10000;
const safeDiv = (a, b) => (b ? a / b : 0);

/**
 * Definições exibidas no painel e gravadas na meta. `source` diz de qual configuração depende
 * (para mostrar "não configurado").
 */
export const INDICATOR_DEFS = [
  { key: 'RB', label: 'Receita Bruta', format: 'currency', requires: ['RB'] },
  { key: 'IMPOSTOS', label: 'Impostos', format: 'currency', requires: ['IMPOSTOS'] },
  { key: 'RL', label: 'Receita Líquida', format: 'currency', requires: ['RL'] },
  { key: 'LB', label: 'Lucro Bruto', format: 'currency', requires: ['LB'] },
  { key: 'EBITDA', label: 'EBITDA', format: 'currency', requires: ['EBITDA'] },
  { key: 'LL', label: 'Lucro Líquido', format: 'currency', requires: ['LL'] },
  { key: 'MARGEM_BRUTA_PCT', label: 'Margem Bruta %', format: 'percent', requires: ['LB', 'RB'] },
  { key: 'EBITDA_PCT', label: 'EBITDA %', format: 'percent', requires: ['EBITDA', 'RB'] },
  { key: 'MARGEM_LIQUIDA_PCT', label: 'Margem Líquida %', format: 'percent', requires: ['LL', 'RL'] },
  { key: 'CUSTOS', label: 'Custos (fixos + variáveis)', format: 'currency', requires: ['CF', 'CV'], any: true },
  { key: 'CUSTOS_PCT', label: 'Custos %', format: 'percent', requires: ['CF', 'CV', 'RB'], any: true },
  { key: 'MC', label: 'Margem de Contribuição', format: 'currency', requires: ['RB', 'CV', 'DV'], any: true },
  { key: 'MC_PCT', label: 'Margem de Contribuição %', format: 'percent', requires: ['RB', 'CV', 'DV'], any: true },
  { key: 'PE', label: 'Ponto de Equilíbrio', format: 'currency', requires: ['RB', 'CF', 'DF'], any: true },
  { key: 'ROI', label: 'ROI', format: 'percent', requires: ['LL', 'INV'] },
];

export const INDICATOR_DEF_BY_KEY = Object.fromEntries(INDICATOR_DEFS.map((d) => [d.key, d]));

/** Indicador disponível = configuração da empresa existe para o que ele precisa. */
export const isIndicatorAvailable = (model, def) => {
  const has = (k) => (model.indicatorCats[k]?.size || 0) > 0 || (model.groupCats[k]?.size || 0) > 0;
  if (def.any) {
    // Fórmulas com RB: RB é obrigatória; dos grupos financeiros basta um configurado.
    const needsRb = def.requires.includes('RB');
    const groups = def.requires.filter((k) => k !== 'RB');
    return (!needsRb || has('RB')) && groups.some(has);
  }
  return def.requires.every(has);
};

/** Séries mensais (12) de cada indicador base a partir dos valores por categoria. */
export const computeBaseIndicatorSeries = (model, catValues) => {
  const out = {};
  const sets = { ...model.indicatorCats, ...model.groupCats };
  BASE_INDICATOR_KEYS.forEach((key) => {
    const series = zeros12();
    (sets[key] || new Set()).forEach((cod) => {
      const values = catValues.get(cod);
      if (!values) return;
      for (let m = 0; m < 12; m += 1) series[m] += values[m];
    });
    out[key] = series;
  });
  return out;
};

/** Agrega os indicadores base num conjunto de meses (soma). */
export const sumBaseIndicators = (baseSeries, months = MONTH_INDEXES) => {
  const sums = {};
  BASE_INDICATOR_KEYS.forEach((key) => {
    let total = 0;
    for (const m of months) total += baseSeries[key]?.[m] || 0;
    sums[key] = total;
  });
  return sums;
};

/** Fórmulas do grupo C sobre um conjunto de somas (um mês, trimestre ou ano). */
export const deriveIndicators = (s) => {
  const rb = s.RB || 0;
  const cv = s.CV || 0;
  const dv = s.DV || 0;
  const cf = s.CF || 0;
  const df = s.DF || 0;
  const mc = rb - Math.abs(cv + dv);
  const mcRatio = trunc4(safeDiv(mc, rb));
  return {
    RB: rb,
    IMPOSTOS: s.IMPOSTOS || 0,
    RL: s.RL || 0,
    LB: s.LB || 0,
    EBITDA: s.EBITDA || 0,
    LL: s.LL || 0,
    MARGEM_BRUTA_PCT: trunc4(safeDiv(s.LB || 0, rb)) * 100,
    EBITDA_PCT: trunc4(safeDiv(s.EBITDA || 0, rb)) * 100,
    MARGEM_LIQUIDA_PCT: trunc4(safeDiv(s.LL || 0, s.RL || 0)) * 100,
    CUSTOS: cf + cv,
    CUSTOS_PCT: trunc4(safeDiv(cf + cv, rb)) * 100,
    MC: mc,
    MC_PCT: mcRatio * 100,
    PE: mcRatio ? Math.abs(cf + df) / mcRatio : 0,
    ROI: safeDiv(s.LL || 0, s.INV || 0) * 100, // backend: sem truncar
  };
};

/** Indicadores derivados mês a mês: { [key]: number[12] }. */
export const monthlyDerivedIndicators = (baseSeries) => {
  const out = Object.fromEntries(INDICATOR_DEFS.map((d) => [d.key, zeros12()]));
  for (let m = 0; m < 12; m += 1) {
    const derived = deriveIndicators(sumBaseIndicators(baseSeries, [m]));
    INDICATOR_DEFS.forEach((d) => { out[d.key][m] = derived[d.key]; });
  }
  return out;
};
